/**
 * Pure helpers for the full-screen keg scanner (KegScanOverlay): list order,
 * progress text, and the checks done before a scan is handed to the screen.
 */
import { isValidKegCode, normalizeKegCode } from './kegCode';
import { extractKegTag } from './kegScanFilter';

/** Scanned tags newest first (screens keep them in scan order, oldest first). */
export function newestFirst(codes: string[]): string[] {
  return [...codes].reverse();
}

export function hasTarget(target: number | null | undefined): target is number {
  return typeof target === 'number' && Number.isFinite(target) && target > 0;
}

export function isComplete(count: number, target: number | null | undefined): boolean {
  return hasTarget(target) && count >= target;
}

/** "7 of 16", "All 16 scanned", or "7 scanned" when there's no target. */
export function progressLabel(count: number, target?: number | null): string {
  if (hasTarget(target)) {
    if (count >= target) return `All ${target} scanned`;
    return `${count} of ${target}`;
  }
  return `${count} scanned`;
}

/**
 * Checks done by the overlay itself before the screen sees a scan. Returns the
 * rejection message, or null when the scan should go to the screen.
 */
export function precheckScan(
  rawCode: string,
  codes: string[],
  target?: number | null,
  /** Camera reads must be EK#####; typed entry keeps the old, looser check. */
  source: 'camera' | 'manual' = 'camera'
): { code: string | null; error: string | null } {
  const code =
    source === 'camera'
      ? extractKegTag(rawCode)
      : isValidKegCode(rawCode)
        ? normalizeKegCode(rawCode)
        : null;
  if (!code) return { code: null, error: 'Not a keg tag (e.g. EK09503)' };
  if (codes.some((c) => normalizeKegCode(c) === code)) return { code, error: `${code} already scanned` };
  if (hasTarget(target) && codes.length >= target) return { code, error: `All ${target} already scanned` };
  return { code, error: null };
}
